import { useState } from "react";
import { useRH } from "../store/rh";
import { useTasks } from "../store/tasks";
import { useSettings } from "../store/settings";
import { useBriefings } from "../store/briefings";
import { generateDailyBriefingAI } from "../lib/claude";
import { PERSONAS } from "../data/org";

const fmt = (ts: number) => new Date(ts).toLocaleString("pt-BR", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
});

export function DailyBriefing() {
  const events     = useRH(s => s.events);
  const employees  = useRH(s => s.employees);
  const tasks      = useTasks(s => s.tasks);
  const settings   = useSettings();
  const briefings  = useBriefings(s => s.briefings);
  const addBriefing = useBriefings(s => s.add);
  const removeBriefing = useBriefings(s => s.remove);

  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    setLoading(true);
    try {
      const hired = Object.values(employees).filter(e => e.status === "hired").map(e => e.personaId);
      const outputs = tasks.flatMap(t =>
        t.outputs.map(o => ({
          taskTitle: t.title,
          persona: PERSONAS.find(p => p.id === o.by)?.name ?? o.by,
          kind: o.kind ?? "text",
          at: o.at
        }))
      ).sort((a, b) => b.at - a.at);

      const text = await generateDailyBriefingAI({
        apiKey: settings.anthropicKey,
        model: settings.model,
        events,
        outputs,
        hiredAgents: hired
      });
      addBriefing(text);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-8 pb-16">
      <header className="border-2 border-ink bg-ink text-bone px-5 py-4 mb-4 flex items-end justify-between">
        <div>
          <div className="font-mono text-xs text-gold">BOL-01</div>
          <h2 className="font-display font-black uppercase text-4xl leading-none">BOLETIM DIÁRIO</h2>
          <div className="font-serif italic mt-1 text-bone/70">
            {briefings.length} edição{briefings.length === 1 ? "" : "ões"} · jornal interno da VPC
          </div>
        </div>
        <button onClick={generate} disabled={loading}
                className="btn bg-bone text-ink border-bone disabled:opacity-50">
          {loading ? "redigindo…" : "✦ gerar boletim de hoje"}
        </button>
      </header>

      {briefings.length === 0 ? (
        <div className="border-2 border-graymd p-16 text-center font-serif italic text-paper">
          Nenhum boletim ainda. Clique "gerar boletim de hoje" — o Claude lê os eventos + entregas e escreve um jornal interno.
        </div>
      ) : (
        <div className="space-y-4">
          {briefings.map(b => (
            <article key={b.id} className="border-2 border-ink bg-bone">
              <header className="bg-ink text-bone px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="tag bg-gold text-ink px-1.5">EDIÇÃO</span>
                  <span className="font-mono text-[12px]">{fmt(b.at)}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => navigator.clipboard.writeText(b.text)}
                          className="tag underline">copiar</button>
                  <button onClick={() => { if (confirm("Apagar?")) removeBriefing(b.id); }}
                          className="tag text-red underline">apagar</button>
                </div>
              </header>
              <pre className="p-6 font-mono text-[13px] whitespace-pre-wrap leading-relaxed">
{b.text}
              </pre>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
